import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../services/adminApiService';
import './AdminComponents.css';

/**
 * Order Book Management Component for Admin
 * Displays order book provider status and allows configuration
 */
const OrderBookManagement: React.FC = () => {
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [mockDataEnabled, setMockDataEnabled] = useState(false);
  const [mockDataStatus, setMockDataStatus] = useState<any>(null);

  useEffect(() => {
    loadProviders();
    loadMockDataStatus();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.get('/api/admin/orderbook/providers');
      if (response.success && response.data) {
        setProviders(response.data);
      } else {
        setError(response.error || 'Failed to load providers');
      }
    } catch (err: any) {
      console.error('Error loading providers:', err);
      setError(err.response?.data?.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const loadMockDataStatus = async () => {
    try {
      const response = await adminApiService.get('/api/admin/orderbook/mock-data/status');
      if (response.success && response.data) {
        setMockDataStatus(response.data);
        setMockDataEnabled(response.data.globalMockEnabled || false);
      }
    } catch (err: any) {
      console.error('Error loading mock data status:', err);
    }
  };

  const testProvider = async (providerName: string) => {
    try {
      setTesting(true);
      setSelectedProvider(providerName);
      setTestResult(null);
      const response = await adminApiService.post(`/api/admin/orderbook/providers/${providerName}/test`, {});
      if (response.success && response.data) {
        setTestResult(response.data);
      } else {
        setTestResult({
          success: false,
          error: response.error || 'Test failed'
        });
      }
    } catch (err: any) {
      console.error('Error testing provider:', err);
      setTestResult({
        success: false,
        error: err.response?.data?.message || 'Test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const toggleMockData = async () => {
    try {
      const response = await adminApiService.post('/api/admin/orderbook/mock-data/toggle', {
        enabled: !mockDataEnabled
      });
      if (response.success && response.data) {
        setMockDataEnabled(response.data.enabled);
        setMockDataStatus(response.data);
      } else {
        setError(response.error || 'Failed to toggle mock data');
      }
    } catch (err: any) {
      console.error('Error toggling mock data:', err);
      setError(err.response?.data?.message || 'Failed to toggle mock data');
    }
  };

  const clearCache = async () => {
    try {
      const response = await adminApiService.post('/api/admin/orderbook/cache/clear', {});
      if (response.success) {
        alert('Cache cleared successfully');
      } else {
        setError(response.error || 'Failed to clear cache');
      }
    } catch (err: any) {
      console.error('Error clearing cache:', err);
      setError(err.response?.data?.message || 'Failed to clear cache');
    }
  };

  if (loading) {
    return (
      <div className="admin-component">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-component">
      <div className="admin-header">
        <h2>Order Book Management</h2>
        <p>Manage order book liquidity providers and view system status</p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c00' }}>
          {error}
        </div>
      )}

      {/* Mock Data Toggle */}
      <div className="admin-section" style={{ marginBottom: '30px', padding: '20px', background: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600' }}>Mock Data Mode</h3>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              {mockDataEnabled ? 'Currently using simulated order book data' : 'Currently using real market data from providers'}
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={mockDataEnabled}
              onChange={toggleMockData}
              style={{ marginRight: '10px', width: '20px', height: '20px' }}
            />
            <span style={{ fontWeight: '500' }}>Use Mock Data</span>
          </label>
        </div>
        {mockDataEnabled && (
          <div style={{ padding: '10px', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
            <strong>⚠️ Warning:</strong> Mock data mode is active. Real market data is disabled.
          </div>
        )}
      </div>

      {/* Providers List */}
      <div className="admin-section">
        <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>Order Book Providers</h3>
        <div style={{ display: 'grid', gap: '20px' }}>
          {providers.map((provider: any) => (
            <div
              key={provider.name}
              style={{
                padding: '20px',
                background: '#fff',
                borderRadius: '8px',
                border: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600' }}>
                  {provider.displayName || provider.name}
                </h4>
                <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                  Status: <span style={{ color: provider.connected ? '#28a745' : '#dc3545', fontWeight: '500' }}>
                    {provider.connected ? 'Connected' : 'Disconnected'}
                  </span>
                </p>
                <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                  Supported Symbols: {provider.supportedSymbols || 0}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => testProvider(provider.name)}
                  disabled={testing}
                  style={{
                    padding: '8px 16px',
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: testing ? 'not-allowed' : 'pointer',
                    opacity: testing ? 0.6 : 1
                  }}
                >
                  {testing && selectedProvider === provider.name ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className="admin-section" style={{ marginTop: '20px', padding: '20px', background: testResult.success ? '#d4edda' : '#f8d7da', borderRadius: '8px', border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}` }}>
          <h4 style={{ margin: '0 0 10px 0', color: testResult.success ? '#155724' : '#721c24' }}>
            Test Result: {testResult.success ? 'Success' : 'Failed'}
          </h4>
          {testResult.error && (
            <p style={{ margin: '5px 0', color: '#721c24' }}>{testResult.error}</p>
          )}
          {testResult.sampleData && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ margin: '5px 0', fontWeight: '500' }}>Sample Data:</p>
              <pre style={{ background: '#fff', padding: '10px', borderRadius: '4px', overflow: 'auto', fontSize: '12px' }}>
                {JSON.stringify(testResult.sampleData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Cache Management */}
      <div className="admin-section" style={{ marginTop: '30px', padding: '20px', background: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
        <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>Cache Management</h3>
        <button
          onClick={clearCache}
          style={{
            padding: '10px 20px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear Order Book Cache
        </button>
      </div>
    </div>
  );
};

export default OrderBookManagement;

